import {
  Avatar,
  FilterBar,
  FilterBarStoreProvider,
  IconDropDownButton,
  InfiniteScrollGrid,
  InfoBar,
  StatusLight
} from "@etframework/ui.components";
import { Query } from "@syncfusion/ej2-data";
import { ColumnDirective } from "@syncfusion/ej2-react-grids";
import { QueriesObserver, useQueryClient } from "@tanstack/react-query";
import { saveAs } from "file-saver";
import moment from "moment";
import { useAuth } from "oidc-react";
import {
  forwardRef,
  Fragment,
  memo,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import emptyStateImg from "../../content/img/taskgrid-empty-state.png";
import { useBoardProvider } from "../../contexts/board/boardContext";
import taskService from "../../data/api/taskService";
import viewService from "../../data/api/viewService";
import {
  useAddTaskCollaborator,
  useMarkTasksAsCompleted
} from "../../data/mutations/taskMutations";
import {
  useBoardCustomFields,
  useTaskUsers
} from "../../data/queries/boardQueries";
import { useSystemPreferences } from "../../data/queries/systemPreferencesQueries";
import { useTags } from "../../data/queries/tagQueries";
import { useTaskTypeByBoardId } from "../../data/queries/taskTypeQueries";
import useCopyLink from "../../hooks/useCopyTaskLink";
import { useDueDatesAuth } from "../../hooks/useDueDatesAuth";
import useEvent from "../../hooks/useEvent";
import { useFilingStatus } from "../../hooks/useFilingStatus";
import { useGridView } from "../../hooks/useGridView";
import useModalEvent from "../../hooks/useModalEvent";
import useRolePermissionStore from "../../store/rolePermissionStore";
import { DateFormats } from "../../types/DateFormats";
import Enums from "../../types/enums";
import { AppDataManager } from "../../utils/dataManager";
import { formatDate } from "../../utils/helpers";
import { IconLoading } from "../Loading/IconLoading";
import { gridTip, gridTipList, GridTooltip } from "./GridTooltip";

// Grid field name for a custom field definition. Must match the backend naming
// (CustomFieldColumns.FieldName): "CF_" + definition id without dashes.
const customFieldName = (definitionId) =>
  `CF_${definitionId.replaceAll("-", "")}`;

const customFieldGridTypes = {
  [Enums.CustomFieldDefinitionType.Number]: "number",
  [Enums.CustomFieldDefinitionType.Date]: "date",
  [Enums.CustomFieldDefinitionType.Boolean]: "boolean",
  [Enums.CustomFieldDefinitionType.Text]: "string"
};

export const TaskGrid = memo(({ enableSaveView, viewId, viewType }) => {
  const { formattedFilters, isPendingView, hiddenColumns, columnOrder, sorts } =
    useGridView(viewId, viewType);

  const filterCheckbox = {
    type: "CheckBox"
  };

  const isFirstRender = useRef(true);
  const rolePermissions = useRolePermissionStore(
    (state) => state.rolePermissions
  );

  const { currentBoardId, currentBoard } = useBoardProvider();
  const { data: taskUsers } = useTaskUsers(currentBoardId);
  const { t, i18n, ready } = useTranslation(["TaskGrid", "Shared"], {
    useSuspense: false
  });
  const { ModalEvents, toggleModal } = useModalEvent();
  const { Events, useRegisterCustomEvent, toggleEvent } = useEvent();
  const { handleCopyLink } = useCopyLink();
  const filingStatus = useFilingStatus();

  const clickedTaskRef = useRef(null);
  const gridRef = useRef(null);
  const collaboratorNotificationRef = useRef(null);

  const { UserId, UserFullName } = useDueDatesAuth();

  const { userManager } = useAuth();

  const dataManager = new AppDataManager(
    "tasks/grid",
    userManager.signinSilent.bind(userManager)
  );

  const gridQuery = useMemo(
    () => new Query().addParams("boardId", currentBoardId),
    [currentBoardId]
  );

  const queryClient = useQueryClient();
  const {
    data: tags,
    isPending: isPendingTags,
    isSuccess: isSuccessTags
  } = useTags();
  const { data: systemPreferences } = useSystemPreferences();
  const {
    data: taskTypes,
    isPending: taskTypesIsPending,
    isSuccess: isSuccessTaskTypes
  } = useTaskTypeByBoardId(currentBoardId);

  const { data: boardCustomFields } = useBoardCustomFields(currentBoardId);

  // One hidden grid column per custom field definition. The field name encodes the
  // definition id (CF_<id without dashes>) — the backend resolves sorting/filtering
  // on it, and each row carries its values under the same keys (flattened in
  // WebApiV4Adaptor). The declared type drives the filter operators and makes the
  // grid send typed OData literals, which the backend matches per definition type.
  const customFieldColumns = useMemo(
    () =>
      (boardCustomFields ?? []).map((def) => {
        const type = customFieldGridTypes[def.Type] ?? "string";
        const field = customFieldName(def.CustomFieldId);

        const booleanTemplate = (props) => {
          let value = props[field];
          if (value === null || value === undefined || value === "")
            value = "false";

          return String(value).toLowerCase() === "true" ? (
            <i className="fa-regular fa-check et-green" />
          ) : (
            <i className="fa-regular fa-xmark et-red" />
          );
        };

        return {
          field,
          headerText: def.Name,
          visible: false,
          allowFiltering: true,
          allowGrouping: false,
          type,
          width: 150,
          minWidth: 120,
          filter: { type: "CheckBox" },
          textAlign:
            type === "boolean" || type === "number" ? "Center" : "Left",
          // Date columns need a column format (not a valueAccessor): the grid
          // coerces date-typed values to Date and renders them through the
          // column's formatter, ignoring accessor output.
          ...(type === "date" && { format: DateFormats.ShortDateFormat }),
          ...(type === "boolean" && {
            template: booleanTemplate
          })
        };
      }),
    [boardCustomFields]
  );

  const { mutate: markTasksAsCompleted } = useMarkTasksAsCompleted();
  const { mutate: addTaskCollaborator } = useAddTaskCollaborator();

  const { getTaskExport } = taskService();

  const id = "dd_task-grid";
  const ids = {
    filterbar: "dd_filter-bar",
    toolbar: {
      refreshGridButton: id + "_toolbar_refresh-grid-button",
      exportTasksButton: id + "_toolbar_export-tasks-button",
      deleteTasksButton: id + "_toolbar_delete-tasks-button",
      bulkUpdateTasksButton: id + "_toolbar_bulk-update-tasks-button",
      copyLinkButton: id + "_toolbar_copy-task-link-button",
      viewTaskButton: id + "_toolbar_view-task-button",
      markTasksAsCompletedButton: id + "_toolbar_mark-as-completed-button"
    },
    moreActions: {
      button: id + "_task-name-cell_more-actions-menu-button",
      deleteTasksButton:
        id + "_task-name-cell_more-actions-menu_delete-tasks-button",
      copyLinkButton:
        id + "_task-name-cell_more-actions-menu_copy-task-link-button",
      viewTaskButton: id + "_task-name-cell_more-actions-menu_view-task-button",
      markTasksAsCompletedButton:
        id + "_task-name-cell_more-actions-menu_complete-tasks-button"
    },
    emptyViewAddTaskButton: id + "_empty-view_add-task-button"
  };

  const key = useMemo(
    () => viewId ?? currentBoardId ?? "none",
    [viewId, currentBoardId]
  );

  function onActionBegin(args) {
    /* Collaborators/Tags popups use a static local datasource (swapped in on
       filterBeforeOpen), so the grid search cannot be applied to their rows:
       keep the distinct query (undoing the shared grid's server-side filtering
       for remote popups) and skip the local search/filter re-apply. */
    if (
      args.requestType === "filterchoicerequest" &&
      ["Collaborators", "Tags"].includes(args.filterModel?.options?.field)
    ) {
      args.query.distincts = [args.filterModel.options.field];
      args.filterModel.customQuery = true;
    }

    //Collaborators
    if (
      args.requestType === "filterBeforeOpen" &&
      args.columnName === "Collaborators"
    ) {
      args.filterModel.options.dataSource = taskUsers?.map((taskUser) => ({
        Collaborators: taskUser?.FullName
      }));

      args.filterModel.options.filteredColumns =
        args.filterModel.options.filteredColumns.filter(
          (x) => x.field === "Collaborators"
        );
    }

    //Tags
    if (args.requestType === "filterBeforeOpen" && args.columnName === "Tags") {
      args.filterModel.options.dataSource = tags.map((tag) => ({
        Tags: tag?.Title
      }));

      args.filterModel.options.filteredColumns =
        args.filterModel.options.filteredColumns.filter(
          (x) => x.field === "Tags"
        );
    }

    // Custom field columns: feed the checkbox filter from the definitions
    // endpoint (already loaded) instead of letting it query distinct values
    // through OData, which dynamic properties don't support.
    if (
      args.requestType === "filterBeforeOpen" &&
      args.columnName?.startsWith("CF_")
    ) {
      const def = boardCustomFields?.find(
        (d) => `CF_${d.CustomFieldId.replaceAll("-", "")}` === args.columnName
      );
      const distinctValues = [
        ...new Set(
          (def?.TaskCustomFields ?? [])
            .map((v) => v.Value)
            .filter((v) => v !== null && v !== "")
        )
      ];

      args.filterModel.options.dataSource = distinctValues.map((value) => ({
        [args.columnName]: value
      }));

      args.filterModel.options.filteredColumns =
        args.filterModel.options.filteredColumns.filter(
          (x) => x.field === args.columnName
        );
    }

    // Custom field columns: feed the checkbox list from the board's custom field
    // values (already loaded) instead of letting the grid query distinct values,
    // and type them so the filter sends typed OData literals.
    if (
      args.requestType === "filterBeforeOpen" &&
      args.columnName?.startsWith("CF_")
    ) {
      const definition = boardCustomFields?.find(
        (def) => customFieldName(def.CustomFieldId) === args.columnName
      );

      if (definition) {
        const parseValue =
          definition.Type === Enums.CustomFieldDefinitionType.Number
            ? (value) => parseFloat(value)
            : definition.Type === Enums.CustomFieldDefinitionType.Date
              ? (value) => new Date(value)
              : (value) => value;

        const distinctValues = [
          ...new Set(
            (definition.TaskCustomFields ?? [])
              .map((value) => value.Value)
              .filter((value) => value !== null && value !== "")
          )
        ];

        args.filterModel.options.dataSource = distinctValues.map((value) => ({
          [args.columnName]: parseValue(value)
        }));

        args.filterModel.options.filteredColumns =
          args.filterModel.options.filteredColumns.filter(
            (x) => x.field === args.columnName
          );
      }
    }

    //Assignee
    if (
      args.requestType === "filtering" &&
      args.currentFilteringColumn == "Assignee"
    ) {
      args.columns.forEach((col, index) => {
        if (col.field === "Assignee") {
          if (col.value === "" || col.value === undefined)
            args.columns.splice(index, 1);
        }
      });
    }

    // Prevent OData groupby operations on collection fields because it cannot group by collection/array properties
    // it only works with simple scalar fields
    if (args.requestType === "filterBeforeOpen") {
      // Remove Collaborators and Tags from the current filter state to prevent groupby
      const collectionFields = ["Collaborators", "Tags"];
      if (args.filterModel?.options?.filteredColumns) {
        args.filterModel.options.filteredColumns =
          args.filterModel.options.filteredColumns.filter((col) => {
            return (
              !collectionFields.includes(col.field) ||
              col.field === args.columnName
            );
          });
      }
    }
  }

  const refreshTaskGrid = () => {
    gridRef?.current?.getComponentRef()?.refresh();
  };

  const exportToExcel = async () => {
    const params = new URLSearchParams(window.location.search);
    const boardId = params.get("boardId");

    toast.promise(
      getTaskExport(boardId).then((response) => {
        const blob = new Blob([response.data]);
        saveAs(blob, response.filename);
      }),
      {
        pending: t("ExportingTasks"),
        success: t("ExportTasksSuccess"),
        error: t("ExportTasksError")
      },
      {
        position: "bottom-left"
      }
    );
  };

  const exportToExcelFromGrid = async () => {
    // Prevent empty export to avoid: Cannot read properties of undefined (reading 'level')  || TypeError: l is undefined
    const totalRecords = gridRef?.current?.getGridRef()?.getTotalRecordsCount();
    if (!totalRecords) return;

    const fn = async () => {
      const excelExportProperties = {
        columns: [
          { field: "Number", headerText: "TaskID" },
          { field: "Client", headerText: "ClientName" },
          {
            field: "TaskTypeKey",
            headerText: "TaskTypeKey"
          },
          { field: "TemplateName", headerText: "TemplateName" },
          { field: "TaxYear", headerText: "TaxYear" },
          { field: "Name", headerText: "TaskName" },
          {
            field: "DueDate",
            valueAccessor: (field, data) => {
              return formatDate(
                data[field],
                DateFormats.ShortDateFormat.momentJsFormat
              ).toString();
            }
          },
          {
            field: "TaxDeadline",
            headerText: "TaxDeadline",
            valueAccessor: (field, data) => {
              return formatDate(
                data[field],
                DateFormats.ShortDateFormat.momentJsFormat
              ).toString();
            }
          },
          {
            field: "ExtendedTaxDeadline",
            headerText: "ExtensionTaxDeadline",
            valueAccessor: (field, data) => {
              return data[field]
                ? formatDate(
                    data[field],
                    DateFormats.ShortDateFormat.momentJsFormat
                  ).toString()
                : "";
            }
          },
          { field: "AssigneeEmail", headerText: "Assignee" },
          { field: "Assignee", headerText: "AssigneeName" },
          {
            field: "Collaborators",
            valueAccessor: (field, data) => {
              return data[field] ?? "";
            }
          },
          { field: "Stage", headerText: "Stage" },
          {
            field: "Tags",
            valueAccessor: (field, data) => {
              return data[field] ?? "";
            }
          },
          {
            field: "FilingStatus",
            headerText: "FillingStatus"
          },
          { field: "Description" },
          { field: "Agency" },
          {
            field: "StartDate",
            valueAccessor: (field, data) => {
              return (
                data[field] &&
                formatDate(
                  data[field],
                  DateFormats.ShortDateFormat.momentJsFormat
                ).toString()
              );
            }
          },
          {
            field: "UpdatedDate",
            headerText: "LastModified",
            valueAccessor: (field, data) => {
              return data[field]
                ? formatDate(
                    data[field],
                    DateFormats.ShortDateFormat.momentJsFormat
                  ).toString()
                : "";
            }
          },
          {
            field: "CreatedDate",
            headerText: "CreatedDate",
            valueAccessor: (field, data) => {
              return data[field]
                ? formatDate(
                    data[field],
                    DateFormats.ShortDateFormat.momentJsFormat
                  ).toString()
                : "";
            }
          },
          ...customFieldColumns.map((column) => ({
            field: column.field,
            headerText: column.headerText,
            valueAccessor: (field, data) => {
              if (data[field] == null) return "";
              return column.type === "date"
                ? formatDate(
                    data[field],
                    DateFormats.ShortDateFormat.momentJsFormat
                  ).toString()
                : data[field];
            }
          }))
        ],
        dataSource: (
          await gridRef.current.getComponentRef().getFilteredRecords()
        ).result,
        fileName: `${currentBoard.Name} ${moment().format(DateFormats.ShortDateFormatHyphen.momentJsFormat)}.xlsx`
      };
      gridRef.current?.getComponentRef()?.excelExport(excelExportProperties);
    };

    if (taskTypesIsPending) {
      const observer = new QueriesObserver(queryClient, [
        { queryKey: ["get-task-types", currentBoardId] }
      ]);
      observer.subscribe((results) => {
        fn(results[0].data);
        observer.destroy();
      });
    } else {
      fn(taskTypes);
    }
  };

  const handleRecordDoubleClick = (e) => {
    toggleEvent(Events.TaskOverlay, { taskId: e.rowData.Id });
  };

  const EmptyTasksTemplate = () => {
    return (
      <div className={`et-taskgrid-empty-state`}>
        <div className="d-flex flex-column justify-content-center align-items-center">
          <img src={emptyStateImg} />
          <div className="et-empty-state-msg mt-3">
            <span className="d-block text-center">
              {t("EmptyStateMessage1")}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const numberTemplate = (props) => {
    const { Number, Id } = props;
    const handleTaskNameButtonClick = () => {
      toggleEvent(Events.TaskOverlay, { taskId: Id });
    };

    return (
      <span
        onClick={handleTaskNameButtonClick}
        className="et-taskgrid-number-value"
      >
        {Number}
      </span>
    );
  };

  const taskNameTemplate = ({ Name, select, index }) => {
    const dropdownMenu = [
      {
        id: ids.moreActions.viewTaskButton + `-${index}`,
        text: t("ViewTask"),
        iconCss: "fa-regular fa-eye",
        onClick: () => {
          toggleEvent(Events.TaskOverlay, {
            taskId: clickedTaskRef.current.Id
          });
        }
      },
      {
        id: ids.moreActions.copyLinkButton + `-${index}`,
        text: t("CopyLink"),
        iconCss: "fa-regular fa-link",
        onClick: () => {
          handleCopyLink(
            clickedTaskRef.current.Id,
            () => {
              toast.success(t("LinkSuccess"), {
                position: "bottom-left"
              });
            },
            () => {
              toast.error(t("LinkError"), {
                position: "bottom-left"
              });
            }
          );
        }
      },
      {
        id: ids.moreActions.markTasksAsCompletedButton + `-${index}`,
        text: t("MarkTasksAsCompleted"),
        iconCss: "fa-regular fa-check",
        disabled: !rolePermissions?.CanComplete,
        onClick: () => {
          markTasksAsCompleted(
            { taskIds: { taskIds: [clickedTaskRef.current.Id] } },
            {
              onSuccess: () => {
                toggleEvent(Events.RefreshTaskGrid);
                toast.success(t("MarkTasksAsCompletedSuccess"), {
                  position: "bottom-left"
                });
              },
              onError: () => {
                toast.error(t("ErrorMsg"), { position: "bottom-left" });
              }
            }
          );
        }
      },
      {
        id: ids.moreActions.deleteTasksButton + `-${index}`,
        text: t("DeleteTask"),
        iconCss: "fa-regular fa-trash-can",
        disabled: !rolePermissions?.CanDelete,
        onClick: () => {
          toggleModal(ModalEvents.DeleteConfirmationModal, {
            name: [clickedTaskRef.current.Name],
            client: [clickedTaskRef.current.Client],
            requestId: [clickedTaskRef.current.Id]
          });
        }
      }
    ];

    const onSelectDropDownTaskMenu = (args) => {
      args?.item?.onClick?.();
      select?.(args);
    };

    const onOpenDropDown = (args) => {
      setTimeout(() => {
        const canModify =
          !rolePermissions?.CanModifyTasks ||
          UserFullName === clickedTaskRef.current?.Assignee ||
          clickedTaskRef.current?.Collaborators?.includes(UserFullName);

        var canDeleteDropdown = args.items?.find(
          (x) => x.text == t("DeleteTask")
        );
        canDeleteDropdown.disabled = !(rolePermissions?.CanDelete && canModify);

        var canCompleteDropDown = args.items?.find(
          (x) => x.text == t("MarkTasksAsCompleted")
        );
        canCompleteDropDown.disabled = !(
          rolePermissions?.CanComplete && canModify
        );
      });
    };

    return (
      <div className="d-flex align-items-center et-taskgrid-taskname-value">
        <div {...gridTip(Name, "d-inline text-truncate")}>
          <span className="fs-6 fw-semibold taskname-value">{Name}</span>
        </div>
        <div {...gridTip(t("MoreActions"), "d-inline")}>
          <IconDropDownButton
            id={ids.moreActions.button + `-${index}`}
            iconCss="fa-regular fa-ellipsis-vertical"
            className="et-taskgrid-taskname-btn"
            select={onSelectDropDownTaskMenu}
            items={dropdownMenu}
            beforeOpen={onOpenDropDown}
          />
        </div>
      </div>
    );
  };

  const descriptionTemplate = ({ Description }) => {
    return (
      <div className="d-flex align-items-center et-taskgrid-description-value">
        <div {...gridTip(Description, "d-inline text-truncate")}>
          <span className="fs-6 description-value">{Description}</span>
        </div>
      </div>
    );
  };

  const clientTemplate = ({ Client }) => {
    return (
      <div {...gridTip(Client)}>
        <div className="text-truncate fs-6">
          <span>{Client}</span>
        </div>
      </div>
    );
  };

  const assigneeTemplate = (props) => {
    const { Assignee } = props;

    return (
      <div className="d-flex align-items-center">
        <Avatar content={Assignee}></Avatar>
        <span className={`ms-2 ${!Assignee ? "et-darkest-gray" : ""}`}>
          {Assignee ?? t("Shared:Unassigned")}
        </span>
      </div>
    );
  };

  const stageTemplate = ({ Stage, StageColor }) => {
    return <StatusLight color={StageColor} text={Stage} />;
  };

  // "Now" and the approaching-date window are the same for every row, so they
  // are built once per render instead of once per cell — the date columns used
  // to allocate two moments (plus a clone) for each of the thousand-odd cells
  // the grid renders.
  const today = moment.utc(new Date());
  const approachingWindowEnd = today
    .clone()
    .add(systemPreferences?.TaskApproachingDaysPrior ?? 0, "days");

  const duedateTemplate = (props) => {
    const { DueDate, StageType } = props;
    let className = "";

    const dueDate = moment.utc(DueDate);

    if (dueDate < today && StageType != Enums.StageType.Completed)
      className = "et-taskgrid-duedate-overdue";

    // Same test as before, shifted onto the row's date: the old code asked
    // whether today fell in [dueDate - N days, dueDate].
    const isInRange = dueDate?.isBetween(
      today,
      approachingWindowEnd,
      "day",
      "[]"
    );

    if (isInRange) className = "et-taskgrid-approaching-date";

    return (
      <div className="et-taskgrid-duedate d-flex justify-content-between align-items-center">
        <span className={className}>
          {formatDate(DueDate, DateFormats.ShortDateFormat.momentJsFormat)}
        </span>
      </div>
    );
  };

  const taxDeadlineTemplate = (props) => {
    const { TaxDeadline, StageType } = props;
    const taxDeadline = moment.utc(TaxDeadline);

    let className = "";
    if (taxDeadline < today && StageType != Enums.StageType.Completed)
      className = "et-taskgrid-duedate-overdue";

    return (
      <>
        {TaxDeadline && (
          <div className="et-taskgrid-duedate">
            <span className={className}>
              {formatDate(
                TaxDeadline,
                DateFormats.ShortDateFormat.momentJsFormat
              )}
            </span>
          </div>
        )}
      </>
    );
  };

  const extendedTaxDeadlineTemplate = (props) => {
    const { ExtendedTaxDeadline, StageType } = props;
    const extendedTaxDeadline = moment.utc(ExtendedTaxDeadline);

    let className = "";
    if (extendedTaxDeadline < today && StageType != Enums.StageType.Completed)
      className = "et-taskgrid-duedate-overdue";

    return (
      <>
        {ExtendedTaxDeadline && (
          <div className="et-taskgrid-duedate">
            <span className={className}>
              {formatDate(
                ExtendedTaxDeadline,
                DateFormats.ShortDateFormat.momentJsFormat
              )}
            </span>
          </div>
        )}
      </>
    );
  };

  const collaboratorsTemplate = ({ Collaborators }) => {
    Collaborators ??= [];

    if (!Collaborators) return "-";

    const maxCollaboratorsShown = 1;

    const getCollaboratorsName = () => {
      const collaboratorsLength = Collaborators.length;
      const collaborators = Collaborators.slice(
        0,
        Math.min(maxCollaboratorsShown, collaboratorsLength)
      );

      return (
        <div {...gridTipList(Collaborators)}>
          <div className="d-flex">
            <span className="et-taskgrid-collaborator-name">
              {collaborators}
            </span>
            {collaboratorsLength > maxCollaboratorsShown && (
              <span className="et-taskgrid-collaborator-length">
                +{collaboratorsLength - maxCollaboratorsShown}
              </span>
            )}
          </div>
        </div>
      );
    };
    return <span className="">{getCollaboratorsName()}</span>;
  };

  const tagsTemplate = ({ Tags }) => {
    Tags ??= [];

    const maxTagsShown = 2;
    const getTagNames = () => {
      const tagsLength = Tags.length;
      const tags = Tags.slice(0, Math.min(maxTagsShown, tagsLength));

      return (
        <div {...gridTipList(Tags)}>
          <div className="d-flex">
            {tags.map((tag, index) => {
              return (
                <div key={index} className="et-taskgrid-tag-name">
                  {tag}
                </div>
              );
            })}
            {tagsLength > maxTagsShown && (
              <span className="et-taskgrid-tag-length">
                +{tagsLength - maxTagsShown}
              </span>
            )}
          </div>
        </div>
      );
    };

    return <span className="">{getTagNames()}</span>;
  };

  const taxYearTemplate = ({ TaxYear }) => TaxYear;

  const agencyTemplate = ({ Agency }) => Agency;

  const startDateTemplate = (props) => {
    const { StartDate } = props;

    const formattedDate = StartDate
      ? formatDate(StartDate, DateFormats.ShortDateFormat.momentJsFormat)
      : "-";

    return <span>{formattedDate}</span>;
  };

  const filingStatusTemplate = (props) => {
    let color =
      filingStatus?.data?.find((x) => x.Id == props?.FilingStatus)?.Color ?? "";
    let text =
      filingStatus?.data?.find((x) => x.Id == props?.FilingStatus)?.Title ?? "";

    if (color != "" && text != "")
      return <StatusLight color={color} text={text} />;
    else return "-";
  };

  const updatedTemplate = (props) => {
    const { UpdatedDate } = props;

    const formattedDate = UpdatedDate
      ? formatDate(
          UpdatedDate,
          DateFormats.ShortDateTimeFormat.momentJsFormatUpperA
        )
      : "-";

    return <span>{formattedDate}</span>;
  };

  const createdDateTemplate = (props) => {
    const { CreatedDate } = props;

    const formattedDate = CreatedDate
      ? formatDate(
          CreatedDate,
          DateFormats.ShortDateTimeFormat.momentJsFormatUpperA
        )
      : "-";

    return <span>{formattedDate}</span>;
  };

  const toolbarCommonOptions = {
    align: "Left",
    type: "Button"
  };

  const ExportTasks = {
    ...toolbarCommonOptions,
    id: ids.toolbar.exportTasksButton,
    visible: true,
    text: t("ExportTasks"),
    prefixIcon: "fa-regular fa-upload",
    click: () => {
      exportToExcelFromGrid();
    },
    enabled: true
  };

  const RefreshGrid = {
    ...toolbarCommonOptions,
    id: ids.toolbar.refreshGridButton,
    visible: true,
    text: t("Shared:Refresh"),
    prefixIcon: "fa-regular fa-arrows-rotate",
    click: () => {
      refreshTaskGrid();
    },
    enabled: true
  };

  const ViewTask = {
    ...toolbarCommonOptions,
    id: ids.toolbar.viewTaskButton,
    visible: false,
    text: t("ViewTask"),
    prefixIcon: "fa-regular fa-eye",
    click: () => {
      toggleEvent(Events.TaskOverlay, { taskId: clickedTaskRef?.current.Id });
    },
    enabled: true
  };

  const CopyTaskLink = {
    ...toolbarCommonOptions,
    id: ids.toolbar.copyLinkButton,
    visible: false,
    text: t("CopyLink"),
    prefixIcon: "fa-regular fa-link",
    click: () => {
      handleCopyLink(
        clickedTaskRef.current.Id,
        () => {
          toast.success(t("LinkSuccess"), {
            position: "bottom-left"
          });
        },
        () => {
          toast.error(t("LinkError"), {
            position: "bottom-left"
          });
        }
      );
    },
    enabled: true
  };

  const MarkTasksAsCompleted = {
    ...toolbarCommonOptions,
    id: ids.toolbar.markTasksAsCompletedButton,
    visible: false,
    text: t("MarkTasksAsCompleted"),
    prefixIcon: "fa-regular fa-check",
    click: () => {
      const selectedRows = gridRef?.current
        ?.getComponentRef()
        ?.getSelectedRecords();

      markTasksAsCompleted(
        { taskIds: { taskIds: selectedRows?.map((task) => task.Id) } },
        {
          onSuccess: () => {
            toggleEvent(Events.RefreshTaskGrid);
            toast.success(t("MarkTasksAsCompletedSuccess"), {
              position: "bottom-left"
            });
          },
          onError: () => {
            toast.error(t("ErrorMsg"), { position: "bottom-left" });
          }
        }
      );
    },
    enabled: true
  };

  const DeleteTask = {
    ...toolbarCommonOptions,
    id: ids.toolbar.deleteTasksButton,
    visible: false,
    text: t("DeleteTask"),
    prefixIcon: "fa-regular fa-trash-can",
    click: () => {
      const selectedRows = gridRef?.current
        ?.getComponentRef()
        ?.getSelectedRecords();

      toggleModal(ModalEvents.DeleteConfirmationModal, {
        name: selectedRows?.map((task) => task.Name).join(", "),
        client: selectedRows?.map((task) => task.Client).join(", "),
        requestId: selectedRows?.map((task) => task.Id)
      });
    },
    enabled: true
  };

  // const CloseToolBar = {
  //   align: "Right",
  //   type: "Button",
  //   id: ids.toolbar.closeButton,
  //   visible: false,
  //   prefixIcon: "fa-regular fa-x",
  //   click: () => {
  //     gridRef?.current?.getComponentRef()?.clearSelection();
  //   },
  //   enabled: true,
  // };

  const BulkUpdateTasks = {
    ...toolbarCommonOptions,
    id: ids.toolbar.bulkUpdateTasksButton,
    visible: false,
    text: t("BulkUpdateTasks"),
    prefixIcon: "fa-regular fa-pencil",
    click: () => {
      const selectedRows = gridRef?.current
        ?.getComponentRef()
        ?.getSelectedRecords();

      toggleModal(ModalEvents.BulkUpdateTaskModalFromGrid, {
        tasks: selectedRows
      });
    },
    enabled: true
  };

  const toolbarItems = [
    ExportTasks,
    RefreshGrid,
    ViewTask,
    CopyTaskLink,
    MarkTasksAsCompleted,
    DeleteTask,
    "ColumnChooser"
  ];

  if (import.meta.env.PUBLIC_BULK_UPDATE_ENABLED == "true") {
    toolbarItems.splice(2, 0, BulkUpdateTasks);
  }

  const searchSettings = {
    fields: [
      "Number",
      "Name",
      "Assignee",
      "Stage",
      "Collaborators",
      "Tags",
      "Client"
    ],
    operator: "contains",
    ignoreCase: true,
    ignoreAccent: true,
    align: "Right"
  };

  if (currentBoard?.IsDefault) {
    searchSettings.fields.push("TaxYear");
  }

  // Text custom fields are searchable too (contains() works on their string
  // subquery). Number/Date fields are excluded: the backend binds those as
  // typed expressions, which contains() cannot apply to.
  searchSettings.fields.push(
    ...(boardCustomFields ?? [])
      .filter((def) => def.Type === Enums.CustomFieldDefinitionType.Text)
      .map((def) => customFieldName(def.CustomFieldId))
  );

  const canEditTask = (task) => {
    // Collaborators may arrive as a JSON string (e.g. '["User A","User B"]') or as an array; normalize it.
    let collaborators = [];
    const raw = task?.Collaborators;
    if (Array.isArray(raw)) {
      collaborators = raw;
    } else if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) collaborators = parsed;
      } catch {
        collaborators = [];
      }
    }

    const canEdit =
      (task?.Assignee === null && collaborators.length === 0) ||
      task?.Assignee === UserFullName ||
      collaborators.includes(UserFullName);
    return canEdit;
  };

  const CollaboratorNotification = forwardRef((props, ref) => {
    const [showNotification, setShowNotification] = useState({});
    const tasks =
      gridRef.current?.getComponentRef()?.getSelectedRecords() ?? [];

    let actions = [];
    if (tasks.length === 1 && !canEditTask(tasks[0])) {
      actions = [
        {
          onClick: () => updateCollaborators(tasks[0]),
          label: t("CollaboratorsMsgAction"),
          className: "btn btn-tertiary"
        }
      ];
    }

    const updateCollaborators = (task) => {
      var collaboratorsUpdated = {
        TaskId: task.Id,
        UserId: UserId
      };

      addTaskCollaborator(
        { collaborators: collaboratorsUpdated },
        {
          onSuccess: (response) => {
            toast.success(response.Message, { position: "bottom-left" });
          },
          onError: () => {}
        }
      );
    };

    useImperativeHandle(ref, () => ({
      setShowNotification
    }));

    return (
      <>
        {showNotification.show && (
          <div className="mb-2 collaborator-notification">
            <InfoBar
              variant="information"
              title={t("CollaboratorsMsg")}
              iconCss="fa-regular fa-circle-info"
              rounded
              actions={showNotification.rowsSelected === 1 ? actions : []}
            />
          </div>
        )}
      </>
    );
  });

  const onRowSelected = (_, selectedCount) => {
    const tasks = gridRef.current.getComponentRef().getSelectedRecords();

    const allEditable = Array.isArray(tasks) && tasks.every(canEditTask);

    const canModify = !rolePermissions?.CanModifyTasks || allEditable;

    const showMarkAsCompleted = rolePermissions?.CanComplete && canModify;
    const showDelete = rolePermissions.CanDelete && canModify;

    collaboratorNotificationRef.current?.setShowNotification({
      show: !canModify,
      rowsSelected: selectedCount
    });

    toolbarItems.forEach((item) => {
      switch (item.id) {
        case ids.toolbar.exportTasksButton:
          item.enabled = selectedCount === 0;
          break;
        case ids.toolbar.refreshGridButton:
          item.enabled = selectedCount === 0;
          break;
        case ids.toolbar.viewTaskButton:
          item.enabled = selectedCount === 1;
          break;
        case ids.toolbar.copyLinkButton:
          item.enabled = selectedCount === 1;
          break;
        case ids.toolbar.markTasksAsCompletedButton:
          item.enabled = showMarkAsCompleted;
          break;
        case ids.toolbar.deleteTasksButton:
          item.enabled = showDelete;
          break;
        case ids.toolbar.bulkUpdateTasksButton:
          item.enabled = canModify;
          break;
        default:
          break;
      }
    });

    let itemsToShow = [];

    itemsToShow = toolbarItems
      ?.filter((item) => item.enabled)
      .map((item) => item.id);

    gridRef?.current?.setToolbarItemVisibility(itemsToShow, true, true);
  };

  const onRowDeselected = (args, selectedCount) => {
    const tasks = gridRef.current.getComponentRef().getSelectedRecords();
    const canModify =
      !rolePermissions?.CanModifyTasks ||
      tasks.reduce((res, curr) => res && canEditTask(curr), true);

    collaboratorNotificationRef.current?.setShowNotification({
      show: !canModify,
      rowsSelected: selectedCount
    });

    if (selectedCount === 0) {
      gridRef?.current?.setToolbarItemVisibility(
        [
          "search-input",
          ids.toolbar.exportTasksButton,
          ids.toolbar.refreshGridButton,
          "ColumnChooser"
        ],
        true,
        true
      );
    } else if (selectedCount >= 1) onRowSelected(args, selectedCount);
  };

  const onRecordClick = (args) => {
    clickedTaskRef.current = args.rowData;
  };

  useRegisterCustomEvent(Events.RefreshTaskGrid, refreshTaskGrid);
  useRegisterCustomEvent(Events.ExportTaskGridToExcel, exportToExcel);

  const onSaveView = async ({ filters, hiddenColumns, columnOrder, sorts }) => {
    const { saveView } = viewService();
    const payload = {
      filters: filters?.map((filter) => {
        return {
          ColumnName: filter.column,
          Operator: filter.operator,
          Value: filter.value
        };
      }),
      hiddenColumns: hiddenColumns
        ?.filter((x) => !!x)
        .map((column) => {
          return {
            columnName: column
          };
        }),
      columnOrder: {
        columnNames: columnOrder
      },
      sorts: sorts?.map((sort) => {
        return {
          columnName: sort.field,
          order: Enums.SortOrder[sort.order]
        };
      })
    };

    saveView(viewId, payload)
      .then(() => {
        toast.success("View saved.", {
          position: "bottom-left"
        });
        queryClient.invalidateQueries(["unmanagedgetviews", viewId]);
      })
      .catch(() => {
        toast.error("Error saving view.", {
          position: "bottom-left"
        });
      });
  };

  const formattedHiddenColumns = hiddenColumns?.map((item) => item.ColumnName);

  const formattedColumnOrder = columnOrder?.[0]?.ColumnNames?.split(",");

  const formattedSorts = sorts?.map((sort) => {
    return {
      field: sort.ColumnName,
      direction: sort.OrderAsString
    };
  });

  const setGridCount = () => {
    if (gridRef.current) {
      toggleEvent(Events.SetTaskGridCount, {
        count: gridRef?.current?.getGridRef()?.getTotalRecordsCount()
      });
    }
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, [isFirstRender]);

  useEffect(() => {
    return () => {
      gridRef.current?.getComponentRef()?.destroy();
    };
  }, []);

  if (
    (isPendingTags && !isSuccessTags) ||
    isPendingView ||
    (taskTypesIsPending && !isSuccessTaskTypes) ||
    !ready
  ) {
    return <IconLoading />;
  }

  return (
    <Fragment>
      <CollaboratorNotification ref={collaboratorNotificationRef} />
      <div className="space-y-4">
        <FilterBarStoreProvider>
          <div className="d-block">
            <FilterBar
              id={ids.filterbar}
              key={key + "filterbar"} //DO NOT REMOVE: Mounts the component again. Makes it snappy!
              gridRef={gridRef}
              locale={i18n.language}
              onSave={onSaveView}
              filterOnSave={false}
              enableSaveView={enableSaveView}
              nullText={{ Assignee: t("Shared:Unassigned") }}
            />
          </div>
          {/*
           * IMPORTANT:
           * There is just one tooltip for the whole grid. Always use this
           * tooltip and do not add tooltips to the column-templates.
           *
           * Otherwise you'll be mounting thousands of tooltips.
           */}
          <GridTooltip>
            <div className="et-task-grid position-relative">
              <InfiniteScrollGrid
                key={key} //DO NOT REMOVE: Mounts the component again. Makes it snappy!
                id={id}
                className="et-taskgrid"
                width="100%"
                showHeader={false}
                actionBegin={onActionBegin}
                searchSettings={searchSettings}
                dataSource={dataManager}
                query={gridQuery}
                allowExcelExport={true}
                allowResizing={true}
                // We need a fallback height, so height is never zero.
                // Height falling back to zero can cause the grid/menus to be stuck
                // on an infinite loop due to 0-division returning infinity. (PF#159382)
                height="56px"
                sortSettings={{
                  columns: [{ field: "Number", direction: "Descending" }]
                }}
                ref={gridRef}
                recordDoubleClick={handleRecordDoubleClick}
                toolbarItems={toolbarItems}
                rowSelected={onRowSelected}
                rowDeselected={onRowDeselected}
                recordClick={onRecordClick}
                dataBound={setGridCount}
                allowReordering={true}
                initialFilters={formattedFilters}
                initialHiddenColumns={formattedHiddenColumns}
                initialColumnOrder={formattedColumnOrder}
                initialSorts={formattedSorts}
                language={i18n.language}
                autoHeight={true}
                emptyRecordTemplate={EmptyTasksTemplate}
                showColumnChooser={true}
                statelessTemplates={["directiveTemplates"]} // PF 159355: This prop cannot be removed
              >
                <ColumnDirective
                  field="Number"
                  type="string"
                  headerText={t("Number")}
                  template={numberTemplate}
                  allowReordering={false}
                  filter={filterCheckbox}
                  autoFit={true}
                />
                <ColumnDirective
                  field="Name"
                  type="string"
                  headerText={t("TaskName")}
                  template={taskNameTemplate}
                  allowReordering={false}
                  filter={filterCheckbox}
                  minWidth={200}
                  width={200}
                />
                <ColumnDirective
                  field="Description"
                  type="string"
                  headerText={t("Description")}
                  template={descriptionTemplate}
                  filter={filterCheckbox}
                  minWidth={200}
                  width={200}
                />
                <ColumnDirective
                  field="Client"
                  type="string"
                  headerText={t("Client")}
                  template={clientTemplate}
                  filter={filterCheckbox}
                  minWidth={200}
                  width={200}
                />
                <ColumnDirective
                  field="Stage"
                  template={stageTemplate}
                  headerText={t("Stage")}
                  filter={filterCheckbox}
                  autoFit={true}
                />
                <ColumnDirective
                  field="DueDate"
                  type="date"
                  headerText={t("DueDate")}
                  template={duedateTemplate}
                  allowFiltering={true}
                  autoFit={true}
                />
                <ColumnDirective
                  field="TaxDeadline"
                  type="date"
                  headerText={t("TaxDeadline")}
                  template={taxDeadlineTemplate}
                  allowFiltering={true}
                  autoFit={true}
                  visible={currentBoard?.IsDefault}
                />
                <ColumnDirective
                  field="ExtendedTaxDeadline"
                  type="date"
                  template={extendedTaxDeadlineTemplate}
                  headerText={t("Extension")}
                  allowFiltering={true}
                  autoFit={true}
                  visible={currentBoard?.IsDefault}
                />
                <ColumnDirective
                  field="Assignee"
                  type="string"
                  template={assigneeTemplate}
                  headerText={t("Assignee")}
                  autoFit={true}
                  minWidth={200}
                  filter={{
                    type: "CheckBox",
                    itemTemplate: ({ Assignee }) => {
                      return Assignee ?? t("Shared:Unassigned");
                    }
                  }}
                />
                <ColumnDirective
                  field="StageType"
                  visible={false}
                  showInColumnChooser={false}
                  disabled={true}
                  headerText={t("StageType")}
                  filter={filterCheckbox}
                />
                <ColumnDirective
                  field="Collaborators"
                  headerText={t("Collaborators")}
                  template={collaboratorsTemplate}
                  filter={filterCheckbox}
                  autoFit={true}
                  minWidth={200}
                  type="string"
                  allowGrouping={false}
                  valueAccessor={(field, data) => {
                    const value = data[field];
                    if (Array.isArray(value)) return value.join(", ");
                    else return null;
                  }}
                />
                <ColumnDirective
                  field="Tags"
                  template={tagsTemplate}
                  headerText={t("Tags")}
                  autoFit={true}
                  isCSV={true}
                  filter={{ ...filterCheckbox, ignoreCase: false }}
                  type="string"
                  allowGrouping={false}
                  valueAccessor={(field, data) => {
                    const value = data[field];
                    if (Array.isArray(value)) return value.join(", ");
                    else return null;
                  }}
                />
                <ColumnDirective
                  field="FormCode"
                  type="string"
                  headerText={t("Form")}
                  visible={false}
                  showInColumnChooser={true}
                  filter={filterCheckbox}
                />
                <ColumnDirective
                  field="TaxYear"
                  type="string"
                  template={taxYearTemplate}
                  headerText={t("TaxYear")}
                  filter={filterCheckbox}
                  autoFit={true}
                  visible={currentBoard?.IsDefault}
                />
                <ColumnDirective
                  field="FilingStatus"
                  template={filingStatusTemplate}
                  headerText={t("FilingStatus")}
                  autoFit={true}
                  visible={currentBoard?.IsDefault}
                  filter={{
                    type: "CheckBox",
                    itemTemplate: ({ FilingStatus }) => {
                      const filingStatuses = {
                        1: t("Filed"),
                        2: t("NotFiled"),
                        3: t("WaitingResponse"),
                        4: t("Error"),
                        5: t("Unknown"),
                        null: "Blank"
                      };

                      return filingStatuses[FilingStatus];
                    }
                  }}
                />
                <ColumnDirective
                  field="Agency"
                  type="string"
                  template={agencyTemplate}
                  headerText={t("Agency")}
                  filter={filterCheckbox}
                  autoFit={true}
                  visible={currentBoard?.IsDefault}
                />
                <ColumnDirective
                  field="StartDate"
                  type="date"
                  template={startDateTemplate}
                  headerText={t("StartDate")}
                />
                <ColumnDirective
                  field="UpdatedDate"
                  type="date"
                  template={updatedTemplate}
                  headerText={t("Updated")}
                />
                <ColumnDirective
                  field="CreatedDate"
                  type="date"
                  template={createdDateTemplate}
                  headerText={t("CreatedDate")}
                />
                {/* Excel Export Fields */}
                <ColumnDirective
                  field="TaskTypeKey"
                  visible={false}
                  showInColumnChooser={false}
                  disabled={true}
                  headerText={"TaskTypeKey"}
                  filter={filterCheckbox}
                />
                <ColumnDirective
                  field="TemplateName"
                  visible={false}
                  showInColumnChooser={false}
                  disabled={true}
                  headerText={"TemplateName"}
                  filter={filterCheckbox}
                />
                <ColumnDirective
                  field="RelatedUsers"
                  visible={false}
                  showInColumnChooser={false}
                  disabled={true}
                  headerText={t("RelatedUsers")}
                  filter={filterCheckbox}
                />
                {/* Custom field columns: hidden by default, available in the column chooser */}
                {customFieldColumns.map((col) => (
                  <ColumnDirective key={col.field} {...col} />
                ))}
              </InfiniteScrollGrid>
            </div>
          </GridTooltip>
        </FilterBarStoreProvider>
      </div>
    </Fragment>
  );
});
